import { ICache } from '../models/cache';
import { ICacheClearArgs } from '../models/cache-clear-args';
import { ICacheReadArgs } from '../models/cache-read-args';
import { ICacheWriteArgs } from '../models/cache-write-args';
import { ICachedValue } from '../models/cached-value';
import {
  cacheRefreshVariance,
  oneHourInMilliseconds,
} from '../models/constants';
import { ISillyLogger } from '../models/silly-logger';
import { getGlobalMap } from '../utility/global-map';
import { now } from '../utility/now';

const pendingReadKey = 'in-a-cache:pending-read';
const pendingRead =
  getGlobalMap<Record<string, Promise<unknown>>>(pendingReadKey);

export abstract class Cache implements ICache {
  protected abstract cacheName: string;

  constructor(protected logger?: ISillyLogger) {}

  public abstract read<T>(args: ICacheReadArgs<T>): Promise<T>;

  public abstract clear(args?: ICacheClearArgs): Promise<void>;

  public abstract clearByBucket(bucket: string): Promise<void>;

  protected async handleCachedRead<T>(
    cached: ICachedValue,
    args: ICacheReadArgs<T>,
    defaultRefreshAfterMilliseconds: number,
  ): Promise<T> {
    const readingHash = this.cacheName + ':' + getHash(args.bucket, args.id);
    const alreadyReading = Boolean(pendingRead[readingHash]);
    const profileCache = process.env.PROFILE_CACHE?.toLowerCase() === 'true';
    if (cached) {
      if (cached.refresh !== 0 && cached.refresh <= now() && !alreadyReading) {
        const promise = args.retrieve();
        pendingRead[readingHash] = promise;
        promise
          .then(value => this.write({ ...args, value }))
          .finally(() => (pendingRead[readingHash] = null));
        if (profileCache && this.logger) {
          this.logger.silly(`cache 🍃 background refresh️ ${readingHash}`);
        }
      } else if (profileCache && this.logger) {
        this.logger.silly(`cache ✅️ ${readingHash}`);
      }
      return cached.value;
    } else {
      try {
        if (profileCache && this.logger) {
          this.logger.silly(
            `cache ❌  ${readingHash}${
              alreadyReading ? ' [joined pending promise]' : ''
            }`,
          );
        }
        const value = await (pendingRead[readingHash] ||
          (pendingRead[readingHash] = args.retrieve()));
        if (!alreadyReading) {
          // Write to the cache in the background without waiting for the result. Gotta go fast!
          this.write({ ...args, value }, defaultRefreshAfterMilliseconds).catch(
            err => {
              if (this.logger) {
                this.logger.silly(`cache write ❌`, err);
              }
            },
          );
        }
        return value as T;
      } finally {
        pendingRead[readingHash] = null;
      }
    }
  }

  public async write<T>(
    args: ICacheWriteArgs<T>,
    defaultRefreshAfterMilliseconds: number = oneHourInMilliseconds,
  ): Promise<void> {
    const refreshAfterMilliseconds =
      args.refreshAfterMilliseconds || defaultRefreshAfterMilliseconds;
    this.saveToCache({
      bucket: args.bucket,
      hash: getHash(args.bucket, args.id),
      refresh:
        args.refreshAfterMilliseconds !== 0
          ? now() +
            refreshAfterMilliseconds +
            Math.round(
              Math.random() * refreshAfterMilliseconds * cacheRefreshVariance,
            )
          : 0,
      value: args.value,
    });
  }

  protected abstract saveToCache(cached: ICachedValue);
}

export function getHash(bucket: string, id: string | number): string {
  return `${bucket}:${id}`;
}
